import React, { useState, useEffect, useRef } from "react";
import { useTheme } from "@mui/material";
import { useRouter } from "next/router";
import { useStore } from "outstated";
import { AppStore } from "pages/_app.js";
import dayjs from "dayjs";

// mui
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import Skeleton from "@mui/material/Skeleton";
import Alert from "@mui/material/Alert";

import InputAdornment from "@mui/material/InputAdornment";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ButtonBase from "@mui/material/ButtonBase";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

// lib
import formatMoney from "lib/formatMoney";
import scroller from "lib/scroller";
import sleep from "lib/sleep";
// components
import AmortizationSchedule from "components/Calculators/LandLoanCalculator/components/AmortizationSchedule";
import HUDForm from "components/Calculators/LandLoanCalculator/components/HUDForm";
import SpreadLTVTable from "components/Calculators/DSCRCalculator/components/SpreadLTVTable";
import CalculatorQuestions from "components/Calculators/LandLoanCalculator/components/CalculatorQuestions";
import getDSCRLTVTiers from "components/Loans/utils/getDSCRLTVTiers";

import loadCalculation from "components/Calculators/components/utils/loadCalculation";
import createCalc from "components/Calculators/DSCRCalculator/utils/createCalc";
import updateCalc from "components/Calculators/DSCRCalculator/utils/updateCalc";
import * as gtag from "components/GTagManager";
import dynamic from "next/dynamic";
const AddressAutocomplete = dynamic(() =>
  import("components/AddressAutocomplete")
);
import SaveSharePDF from "components/Calculators/components/SaveSharePDF";
import PayoffChart from "components/Calculators/LandLoanCalculator/components/PayoffChart";
import QuestionRenderer from "components/Calculators/components/QuestionRenderer";
import Authenticator from "components/Authenticator";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";

// const payoutDict = {
//   homeInsurance: "Home insurance",
//   mortgageInsurance: "Mortgage insurance",
//   countyTax: "County tax",
//   cityTax: "City tax",
//   schoolTax: "School tax",
//   floodInsurance: "Flood insurance",
// };

// const exampleCalc = {
// //   "Purchase price": 100000,
// //   "Loan to value": 75,
// //   "Amortization type": "Principal and interest",
// //   Term: 30,
// //   "Interest rate": 7.99,
// //   "Property tax": 2000,
// //   Insurance: 800,
// //   "Loan payment frequency": "Monthly",
// //   "Calculation name": "5 Acre Land Loan Example",
// //   "Address (optional)": "123 Rural Route, Anytown, USA",
// // };

// First, define the content div IDs at the top of the component
// const contentDivIds = [
//   "calculator-inputs-1",
//   "calculator-inputs-2",
//   "calculator-inputs-3",
//   "calculator-results",
//   "payoff-chart",
//   "amortization-schedule",
// ];

const calcType = "Land loan";

export default function LandLoanCalculator({
  externalCalc,
  loadCalcs,
  refCalcs,
  externalExampleCalc,
}) {
  const theme = useTheme();
  const router = useRouter();

  const [suggestionChoice, setSuggestionChoice] = useState(false);
  const [HUDSummary, setHUDSummary] = useState({});
  const {
    user,
    userProfile,
    isNewUser,
    setSnackbarData,
    showLogInDialog,
    setShowLogInDialog,
  } = useStore(AppStore);
  const [loanQuoteData, setLoanQuoteData] = useState({});
  const [resultsData, setResultsData] = useState([]);
  const [singleItemAnalysisData, setSingleItemAnalysisData] = useState({});

  const [showQuestionDescription, setShowQuestionDescription] = useState({
    "Monthly cashflow": true,
    "Cash to close": true,
  });
  // Answers is what users give and shown in the UI
  const [answers, setAnswers] = useState([]);
  // Questions is what has blueprint of what we ask
  const [questions, setQuestions] = useState([]);
  // All current session answers are held in this
  const [currentValues, setCurrentValues] = useState({});
  const [loanAmount, setLoanAmount] = useState({});
  const [loanPayment, setLoanPayment] = useState(0);

  const [dscr, setDSCR] = useState(0);
  const [cashToClose, setCashToClose] = useState(0);
  const [monthlyCashflow, setMonthlyCashflow] = useState(0);
  const [showSaveFields, setShowSaveFields] = useState(false);
  const [showUpdateFields, setShowUpdateFields] = useState(false);
  const [calc, setCalc] = useState(externalCalc ? externalCalc : null);
  const [showCreatedSuccessMessage, setShowCreatedSuccessMessage] =
    useState(false);
  const [showMeMyRate, setShowMeMyRate] = useState(false);
  // DSCR
  const [creditScore, setCreditScore] = useState(null);
  const [showCopiedSuccess, setShowCopiedSuccess] = useState(false);
  const [anchorEl, setAnchorEl] = useState({});
  // Add this with other state variables at the top
  const [totalPayment, setTotalPayment] = useState(0);
  const [basePayment, setBasePayment] = useState(0);

  // special section that isn't part of original questions
  // but must be added later so we can still capture in 'answers'
  const firstPartOfQuestions = [
    "Purchase price",
    "Loan to value",
    "Amortization type",
    "Term",
    "Interest rate",
    "Payment frequency",
  ];

  const secondPartOfQuestions = ["Property tax", "Insurance"];

  const thirdPartOfQuestions = [
    "Origination fee",
    "Loan fees",
    "Title fees",
    "Escrow",
    "Prepaid interest",
    "Prepaid insurance",
  ];

  const fourthPart = ["Calculation name", "Address (optional)"];

  // Add this state at the top with other state declarations
  const [showFullSchedule, setShowFullSchedule] = useState(false);

  const handleOpen = (event, calc) => {
    const updatedAnchorEl = Object.assign({}, anchorEl);
    updatedAnchorEl[calc.id] = event.currentTarget;
    setAnchorEl(updatedAnchorEl);
  };

  const handleClose = (calc) => {
    if (calc) {
      const updatedAnchorEl = Object.assign({}, anchorEl);
      delete updatedAnchorEl[calc.id];
      setAnchorEl(updatedAnchorEl);
    }
  };

  // Custom hook to get the previous state
  const usePrevious = (value) => {
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  };
  // Save the previous state
  const previousValues = usePrevious(currentValues);

  const defaultValues = {
    "Amortization type": "Principal and interest",
    "Payment frequency": "Monthly",
  };

  useEffect(() => {
    loadCalculation(
      defaultValues,
      externalCalc,
      externalExampleCalc,
      CalculatorQuestions,
      setQuestions,
      setAnswers,
      setCurrentValues,
      setShowUpdateFields
    );
    // let updatedAnswers = [];
    // let questions = CalculatorQuestions;
    // setQuestions(questions);
    // // extract current DSCR interest rates from capital page's data
    // // const DSCRRateRange = extractDSCRInterestRateRange(CapitalData);
    // // setDSCRRateRange(DSCRRateRange);
    // // if loaded on /my/calculations
    // // where we preload existing/saved calcs
    // if (externalCalc && externalCalc.Answers.length > 0) {
    //   // external calcs, can be 'new' calcs, that don't have answers, so other part of the if is relevant
    //   updatedAnswers = externalCalc.Answers.slice(0);
    //   updatedAnswers.forEach((answer) => {
    //     const [questionWithSameId] = questions.filter(
    //       (question) => question.q_id === answer.q_id
    //     );
    //     if (questionWithSameId) {
    //       // Update the answer with the corresponding question's properties
    //       answer.q_description = questionWithSameId.q_description;
    //       answer.q_title = questionWithSameId.q_title;
    //       answer.node_type = questionWithSameId.node_type;
    //       answer.a_type = questionWithSameId.a_type;
    //       answer.a_input_type = questionWithSameId.a_input_type;
    //       answer.a_opts = questionWithSameId.a_opts;
    //       answer.q_next_step_func = questionWithSameId.q_next_step_func;
    //       answer.q_validation_func = questionWithSameId.q_validation_func;
    //       answer.q_invalid_answer_help_text =
    //         questionWithSameId.q_invalid_answer_help_text;
    //       answer.a_error = questionWithSameId.a_error;
    //     }
    //   });
    //   setAnswers(updatedAnswers);
    //   setShowUpdateFields(true);
    // } else {
    //   let q_id_counter = "";
    //   let answers = [];
    //   const questionsSlice = questions.slice(); // make sure to make a copy
    //   let leftMostPath = questionsSlice.filter((question) => {
    //     // https://stackoverflow.com/a/21596128/1933331
    //     return /^0*$/.test(question.q_id);
    //   });
    //   // leftMostPath
    //   // must sort based on q_id length of 0s inside
    //   // left most paths q_ids
    //   leftMostPath.sort((a, b) => a.q_id.length - b.q_id.length);
    //   // leftMostPath for initial state
    //   // as user selects answers,
    //   // the path deviates and questions ahead change
    //   // this populates empty placeholder answers from initial questions
    //   for (let i = 0; i < leftMostPath.length; i++) {
    //     const question = leftMostPath[i];
    //     // create some defaults and pre-sets
    //     // to reduce users number of click
    //     let preset_a = "";
    //     if (Object.keys(externalExampleCalc).includes(question.q_title)) {
    //       preset_a = externalExampleCalc[question.q_title];
    //     }
    //     q_id_counter = q_id_counter + "0";
    //     answers.push({
    //       a_string: preset_a,
    //       q_id: new Array(i + 2).join("0"),
    //       q_description: question.q_description,
    //       q_title: question.q_title,
    //       node_type: question.node_type,
    //       a_type: question.a_type,
    //       a_input_type: question.a_input_type,
    //       a_opts: question.a_opts,
    //       q_next_step_func: question.q_next_step_func,
    //       q_validation_func: question.q_validation_func,
    //       q_invalid_answer_help_text: question.q_invalid_answer_help_text,
    //       a_error: question.a_error,
    //       q_placeholder: question.q_placeholder,
    //     });
    //   }
    //   updatedAnswers = answers;
    // }
    // setAnswers(updatedAnswers);
    // // based of answers set simple currentValues k:v
    // const initialCurrentValues = {};
    // updatedAnswers.forEach((answer) => {
    //   // Check if answer.q_title matches any key in the default values object
    //   if (defaultValues.hasOwnProperty(answer.q_title) && !answer.a_string) {
    //     initialCurrentValues[answer.q_title] = defaultValues[answer.q_title];
    //   } else {
    //     initialCurrentValues[answer.q_title] = answer.a_string;
    //   }
    // });
    // setCurrentValues(initialCurrentValues);
  }, []);

  useEffect(() => {
    if (Object.keys(currentValues).length > 0) {
      // Get values from inputs
      const purchasePrice = parseFloat(currentValues["Purchase price"]) || 0;
      const ltv = parseFloat(currentValues["Loan to value"]) || 0;
      const term = parseFloat(currentValues["Term"]) || 0;
      const interestRate = parseFloat(currentValues["Interest rate"]) || 0;
      const propertyTax = parseFloat(currentValues["Property tax"]) || 0;
      const insurance = parseFloat(currentValues["Insurance"]) || 0;
      const paymentFreq = currentValues["Payment frequency"] || "Monthly";
      const amortType =
        currentValues["Amortization type"] || "Principal and interest";

      // Calculate loan amount and payment frequency
      const calculatedLoanAmount = purchasePrice * (ltv / 100);
      const paymentsPerYear = {
        Monthly: 12,
        Quarterly: 4,
        "Semi-annual": 2,
        Annual: 1,
      }[paymentFreq];

      // Get all fees for cash to close
      const originationFee = parseFloat(currentValues["Origination fee"]) || 0;
      const loanFees = parseFloat(currentValues["Loan fees"]) || 0;
      const titleFees = parseFloat(currentValues["Title fees"]) || 0;
      const escrowFees = parseFloat(currentValues["Escrow"]) || 0;
      const prepaidInterest =
        parseFloat(currentValues["Prepaid interest"]) || 0;
      const prepaidInsurance =
        parseFloat(currentValues["Prepaid insurance"]) || 0;

      // Calculate total fees
      const totalFees =
        originationFee +
        loanFees +
        titleFees +
        escrowFees +
        prepaidInterest +
        prepaidInsurance;

      const ratePerPeriod = interestRate / 100 / paymentsPerYear;
      const totalPeriods = term * paymentsPerYear;

      // Calculate base payment - for interest only this is just the interest portion
      let basePayment = 0;
      if (amortType === "Principal and interest") {
        basePayment =
          (calculatedLoanAmount *
            (ratePerPeriod * Math.pow(1 + ratePerPeriod, totalPeriods))) /
          (Math.pow(1 + ratePerPeriod, totalPeriods) - 1);
      } else {
        // For interest only, base payment is just the interest
        basePayment = calculatedLoanAmount * ratePerPeriod;
      }

      // Calculate tax and insurance per period
      const taxAndInsPerPeriod = (propertyTax + insurance) / paymentsPerYear;

      // Generate amortization schedule
      const amortizationSchedule = [];
      let remainingBalance = calculatedLoanAmount; // Track remaining balance separately
      const startDate = new Date();

      for (let period = 1; period <= totalPeriods; period++) {
        // Calculate date for this period based on payment frequency
        const paymentDate = new Date();
        switch (paymentFreq) {
          case "Monthly":
            paymentDate.setMonth(paymentDate.getMonth() + period);
            break;
          case "Quarterly":
            paymentDate.setMonth(paymentDate.getMonth() + period * 3);
            break;
          case "Semi-annual":
            paymentDate.setMonth(paymentDate.getMonth() + period * 6);
            break;
          case "Annual":
            paymentDate.setFullYear(paymentDate.getFullYear() + period);
            break;
        }

        // Calculate payment components
        const interestPayment = remainingBalance * ratePerPeriod;
        let principalPayment = 0;
        let loanPayment = basePayment;
        let totalPayment = basePayment;

        if (amortType === "Principal and interest") {
          principalPayment = basePayment - interestPayment;
        } else if (period === totalPeriods) {
          // On final payment for interest-only:
          // 1. Still pay regular interest
          // 2. Add balloon payment of full principal
          principalPayment = remainingBalance;
          loanPayment = interestPayment + principalPayment; // Final payment includes both
        }

        // Update remaining balance
        remainingBalance = remainingBalance - principalPayment;

        // Create row for amortization table
        amortizationSchedule.push({
          date: paymentDate.toLocaleString("default", {
            month: "long",
            year: "numeric",
          }),
          loanPayment: loanPayment, // Use loanPayment which includes balloon on final payment
          totalPayment: totalPayment,
          interest: interestPayment,
          principal: principalPayment,
          balance: remainingBalance,
          isBalloonPayment:
            amortType === "Interest only" && period === totalPeriods,
          taxAndInsurance: taxAndInsPerPeriod,
          totalPayment: loanPayment + taxAndInsPerPeriod,
        });
      }

      // Update state with results
      setResultsData(amortizationSchedule);
      setLoanAmount(calculatedLoanAmount);
      setBasePayment(basePayment);
      setTotalPayment(basePayment + taxAndInsPerPeriod);
      setLoanPayment(basePayment);

      // Calculate cash to close (down payment + fees)
      const downPayment = purchasePrice - calculatedLoanAmount;
      const cashToClose = downPayment + totalFees;
      setCashToClose(cashToClose);
    }
  }, [currentValues]);

  const processSelect = (ltvRange) => {
    const updatedAnswers = answers.slice(0);

    const [toUpdateAnswer] = updatedAnswers.filter((answer) => {
      return answer?.q_title === "Loan to value (LTV)";
    });

    toUpdateAnswer.a_string = ltvRange.LTV;

    const [toUpdateAnswerInterestRate] = updatedAnswers.filter((answer) => {
      return answer?.q_title === "Interest rate";
    });

    toUpdateAnswerInterestRate.a_string = (
      parseFloat(loanQuoteData.treasury5yr.rate) +
      parseFloat(ltvRange.creditSpread)
    ).toFixed(2);

    // clear interest rate in current values so value shows in textField
    const updatedCurrentValues = Object.assign({}, currentValues);

    // reset all current values
    Object.keys(updatedCurrentValues).forEach((key) => {
      updatedCurrentValues[key] = undefined;
    });

    setCurrentValues(updatedCurrentValues);

    // set answers last to recalc
    setAnswers(updatedAnswers);

    setTimeout(() => {
      scroller(`text_field_Purchase price`);
      setShowMeMyRate(false);
    }, 1000);
  };

  const handleUserSelection = (
    value,
    answer,
    answers,
    questions,
    setAnswers
  ) => {
    const questionsSlice = questions.slice();

    const updatedAnswers = answers.slice(0);

    const isValidAnswer = answer.q_validation_func(value, answers);

    if (isValidAnswer) {
      const targetAnswer = updatedAnswers.filter((updatedAnswer) => {
        return updatedAnswer.q_id === answer.q_id;
      })[0];

      // save users answer
      targetAnswer.a_string = value;

      // reset error just in case
      targetAnswer.a_error = false;

      // determine if need to insert follow up questions
      // const next_q_id = answer.q_next_step_func(targetAnswer, answers);
      const next_q_id = answer.q_next_step_func(answer, answers, questions);

      const q_ids = updatedAnswers.map((answer) => {
        return answer.q_id;
      });

      // NEXT QUESTION is always next in position
      // NOT like before when it was based on length of q_id
      // this was limiting in terms of folding questions of the tree back
      // and allowing the tree to merge back to left most path
      const posOfNextQuestion =
        updatedAnswers
          .map((updatedAnswer) => {
            return updatedAnswer.q_id;
          })
          .indexOf(answer.q_id) + 1;

      const nextPotentialQuestion = updatedAnswers[posOfNextQuestion];

      if (
        next_q_id &&
        nextPotentialQuestion &&
        nextPotentialQuestion?.q_id !== next_q_id
      ) {
        // OFF LEFT MOST PATH
        const newNextQuestion = questionsSlice.filter((question) => {
          return question.q_id === next_q_id;
        })[0];

        // LEAF, remove all subsequent questions from answers
        if (newNextQuestion.node_type === "leaf") {
          // if a LEAF never delete anything (like slice)
          // instead add hidden property
          for (let i = posOfNextQuestion; i < updatedAnswers.length; i++) {
            updatedAnswers[i].node_visibility = "hidden";
          }
        } else {
          // NODE, make sure subsequent questions are prents
          // need to make left most path starting from next questions's id.
          let leftMostPathFromCurrentNode = [];
          let deeperNextQuestion = newNextQuestion;
          let deeperNextQuestionChildrenLength =
            deeperNextQuestion.q_child_nodes.length;
          const map_q_id = questionsSlice.map((question) => {
            return question.q_id;
          });

          while (deeperNextQuestionChildrenLength !== 0) {
            deeperNextQuestion =
              questions[
                map_q_id.indexOf(
                  deeperNextQuestion.q_next_step_func(
                    deeperNextQuestion,
                    [],
                    []
                  )
                )
              ];

            if (deeperNextQuestion) {
              deeperNextQuestionChildrenLength =
                deeperNextQuestion.q_child_nodes.length;

              leftMostPathFromCurrentNode.push(deeperNextQuestion);
            } else {
              deeperNextQuestionChildrenLength = 0;
            }
          }

          const earlierQuestions = updatedAnswers.slice(
            0,
            posOfNextQuestion + 2
          );

          let counter = 0;
          for (
            let i = posOfNextQuestion + 1;
            i < leftMostPathFromCurrentNode.length + posOfNextQuestion + 1;
            i++
          ) {
            if (
              updatedAnswers[i]?.q_id === leftMostPathFromCurrentNode[counter]
            ) {
              //show the node
              // updatedAnswers[i].node_type = "node";
            } else {
              //or overwrite if different
              updatedAnswers[i] = leftMostPathFromCurrentNode[counter];
              // updatedAnswers[i].node_type = "node";
            }
            counter = counter + 1;
          }

          // make sure all nodes are re-updated
          // to have node_type be node so nothing is hidden
          // if people jump around
          for (let i = 0; i < updatedAnswers.length; i++) {
            if (updatedAnswers[i].node_type === "node") {
              updatedAnswers[i].node_visibility = "visible";
            }
          }
        }

        updatedAnswers[posOfNextQuestion] = questionsSlice.filter(
          (question) => {
            return question.q_id === next_q_id;
          }
        )[0];
        updatedAnswers[posOfNextQuestion].node_visibility = "visible";
      }

      setAnswers(updatedAnswers);
    } else {
      updatedAnswers.filter((updatedAnswer) => {
        return updatedAnswer.q_id === answer.q_id;
      })[0].a_error = true;

      setAnswers(updatedAnswers);
      return;
    }
  };

  const generateAnswerOptions = (answer, index, answers) => {
    let answerOptionsUI;

    switch (answer.a_type) {
      case "leaf":
        answerOptionsUI = (
          <Box key={`${index}_${answer.a_type}`} sx={{ mb: 2 }}>
            <div></div>
          </Box>
        );
        break;
      case "percent":
        let localValue =
          currentValues[answer.q_title] === undefined
            ? answer.a_string
              ? answer.a_string
              : null
            : !isNaN(currentValues[answer.q_title])
            ? currentValues[answer.q_title]
            : null;

        answerOptionsUI = (
          <Box
            key={`${index}_${answer.a_type}`}
            sx={{
              margin: theme.spacing(1),
              width: "100%",
              [theme.breakpoints.down("md")]: {
                padding: `0 !important`,
              },
            }}
          >
            <TextField
              id={`text_field_${answer.q_title}_${calc?.id ? calc.id : 0}`}
              sx={{
                width: "100%",
                [theme.breakpoints.down("md")]: {
                  padding: `0 !important`,
                },
              }}
              error={answer.a_error}
              InputLabelProps={{ shrink: localValue }}
              label={
                answer.a_error
                  ? "Error"
                  : answer.q_placeholder
                  ? answer.q_placeholder
                  : answer.q_title
              }
              type={answer.a_input_type}
              value={localValue || ""}
              onChange={(e) => {
                const num = e.target.value;
                const updatedCurrentValues = Object.assign({}, currentValues);
                updatedCurrentValues[answer.q_title] = num;

                setCurrentValues(updatedCurrentValues);

                handleUserSelection(
                  num,
                  answer,
                  answers,
                  questions,
                  setAnswers
                );
              }}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              helperText={
                answer.a_error ? answer.q_invalid_answer_help_text : ""
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();

                  const next_q_id = answer.q_next_step_func(
                    answer,
                    answers,
                    questions
                  );

                  if (next_q_id) {
                    const nextQuestion = questions.find(
                      (q) => q.q_id === next_q_id
                    );

                    const nextQuestionTextFieldId = `text_field_${nextQuestion.q_title}_${calc.id}`;
                    const nextQuestionTextField = document.getElementById(
                      nextQuestionTextFieldId
                    );

                    if (nextQuestionTextField) {
                      nextQuestionTextField.focus();
                    }
                  }
                }
              }}
            />
          </Box>
        );
        break;
      case "mc":
        answerOptionsUI = (
          <Box
            key={`${index}_${answer.a_type}`}
            sx={{
              flex: "0 1 100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {answer.a_opts(answers).map((a_opt, index) => {
              {
                return (
                  <Button
                    id={
                      index === 0
                        ? `text_field_${answer.q_title}_${
                            calc?.id ? calc.id : 0
                          }`
                        : `text_field_${answer.q_title}_opt_${index}`
                    }
                    sx={{ margin: theme.spacing(1) }}
                    variant={
                      currentValues?.[answer.q_title] === a_opt
                        ? "contained"
                        : "outlined"
                    }
                    onClick={(e) => {
                      const updatedCurrentValues = Object.assign(
                        {},
                        currentValues
                      );
                      updatedCurrentValues[answer.q_title] = a_opt;

                      setCurrentValues(updatedCurrentValues);

                      handleUserSelection(
                        a_opt,
                        answer,
                        answers,
                        questions,
                        setAnswers
                      );
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        const updatedCurrentValues = Object.assign(
                          {},
                          currentValues
                        );
                        updatedCurrentValues[answer.q_title] = a_opt;

                        setCurrentValues(updatedCurrentValues);

                        handleUserSelection(
                          a_opt,
                          answer,
                          answers,
                          questions,
                          setAnswers
                        );

                        const next_q_id = answer.q_next_step_func(
                          answer,
                          answers,
                          questions
                        );

                        if (next_q_id) {
                          const nextQuestion = questions.find(
                            (q) => q.q_id === next_q_id
                          );

                          const nextQuestionTextFieldId = `text_field_${nextQuestion.q_title}_${calc.id}`;
                          const nextQuestionTextField = document.getElementById(
                            nextQuestionTextFieldId
                          );

                          if (nextQuestionTextField) {
                            nextQuestionTextField.focus();
                          }
                        }
                      }
                    }}
                  >
                    {a_opt}
                  </Button>
                );
              }
            })}
          </Box>
        );
        break;
      case "text":
        const localTextValue =
          currentValues[answer.q_title] === undefined
            ? answer.a_string
              ? answer.a_string
              : null
            : currentValues[answer.q_title];

        answerOptionsUI = (
          <Box key={`${index}_${answer.a_type}`}>
            <TextField
              id={`text_field_${answer.q_title}_${calc?.id ? calc.id : 0}`}
              sx={{ margin: theme.spacing(5, 0) }}
              error={answer.a_error}
              label={
                answer.a_error
                  ? "Error"
                  : answer.q_placeholder
                  ? answer.q_placeholder
                  : answer.q_title
              }
              type={answer.a_input_type}
              value={localTextValue}
              InputLabelProps={{ shrink: localTextValue }}
              onChange={(e) => {
                const val = e.target.value;
                const updatedCurrentValues = Object.assign({}, currentValues);
                updatedCurrentValues[answer.q_title] = val;
                setCurrentValues(updatedCurrentValues);
                handleUserSelection(
                  val,
                  answer,
                  answers,
                  questions,
                  setAnswers
                );
              }}
              helperText={
                answer.a_error ? answer.q_invalid_answer_help_text : ""
              }
            />
          </Box>
        );
        break;
      case "currency":
        const uiValue =
          currentValues[answer.q_title] === undefined
            ? answer.a_string && answer.a_string.length > 0
              ? `$${formatMoney(answer.a_string, 0)}`
              : null
            : !isNaN(currentValues[answer.q_title]) &&
              currentValues[answer.q_title]
            ? `$${formatMoney(currentValues[answer.q_title], 0)}`
            : null;

        answerOptionsUI = (
          <Box
            key={`${index}_${answer.a_type}`}
            sx={{
              display: "flex",
              flexDirection: "column",
              margin: theme.spacing(1),
              fontSize: theme.spacing(3),
              width: "100%",
            }}
          >
            <TextField
              id={`text_field_${answer.q_title}_${calc?.id ? calc.id : 0}`}
              helperText={answer.q_help_text ? answer.q_help_text : ""}
              sx={{ width: "100%" }}
              label={
                answer.a_error
                  ? "Error"
                  : answer.q_placeholder
                  ? answer.q_placeholder
                  : answer.q_title
              }
              type={answer.a_type_input}
              inputProps={{
                inputMode: "numeric", // This makes mobile to pull up numbers only keyboard
              }}
              value={uiValue || ""}
              onChange={(e) => {
                const num = e.target.value.replace(/\D/g, "");

                const updatedCurrentValues = Object.assign({}, currentValues);
                updatedCurrentValues[answer.q_title] = num;
                setCurrentValues(updatedCurrentValues);
                handleUserSelection(
                  num,
                  answer,
                  answers,
                  questions,
                  setAnswers
                );
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();

                  const next_q_id = answer.q_next_step_func(
                    answer,
                    answers,
                    questions
                  );

                  if (next_q_id) {
                    const nextQuestion = questions.find(
                      (q) => q.q_id === next_q_id
                    );

                    const nextQuestionTextFieldId = `text_field_${nextQuestion.q_title}_${calc.id}`;
                    const nextQuestionTextField = document.getElementById(
                      nextQuestionTextFieldId
                    );

                    if (nextQuestionTextField) {
                      nextQuestionTextField.focus();
                    }
                  }
                }
              }}
            />
          </Box>
        );
        break;
      case "number":
        answerOptionsUI = (
          <Box
            key={`${index}_${answer.a_type}`}
            sx={{
              display: "flex",
              flexDirection: "column",
              margin: theme.spacing(1),
              fontSize: theme.spacing(2),
              width: "100%",
            }}
          >
            <TextField
              id={`text_field_${answer.q_title}_${calc?.id ? calc.id : 0}`}
              helperText={
                answer.a_error
                  ? answer.q_invalid_answer_help_text
                  : answer.q_help_text
                  ? answer.q_help_text
                  : ""
              }
              error={answer.a_error}
              label={
                answer.a_error
                  ? "Error"
                  : answer.q_placeholder
                  ? answer.q_placeholder
                  : answer.q_title
              }
              type={answer.a_type_input}
              value={
                currentValues[answer.q_title] === undefined
                  ? answer.a_string
                    ? answer.a_string
                    : null
                  : currentValues[answer.q_title]
              }
              InputLabelProps={{
                shrink:
                  currentValues[answer.q_title] === undefined
                    ? answer.a_string
                      ? answer.a_string
                      : null
                    : currentValues[answer.q_title],
              }}
              onChange={(e) => {
                const value = e.target.value;
                const updatedCurrentValues = Object.assign({}, currentValues);
                updatedCurrentValues[answer.q_title] = value;
                setCurrentValues(updatedCurrentValues);
                handleUserSelection(
                  value,
                  answer,
                  answers,
                  questions,
                  setAnswers
                );

                // this is done to reset
                // 'get rate' output when new stuff is typed in
                setLoanQuoteData({});
              }}
            />
          </Box>
        );
        break;
      case "address":
        const address = calc?.Address;

        answerOptionsUI = (
          <Box
            key={`${index}_${answer.a_type}`}
            id={`text_field_${answer.q_title}_${calc?.id ? calc.id : 0}`}
            sx={{ margin: theme.spacing(3, 0, 3, 0) }}
          >
            <AddressAutocomplete
              autocompleteOnKeyDown={(
                e,
                setSuggestionsVisible,
                setLocalAddress,
                place,
                locationList,
                setLocationList
              ) => {
                if (e.key === "Enter") {
                  setSuggestionChoice(place.formatted_address);

                  handleUserSelection(
                    place.formatted_address,
                    answer,
                    answers,
                    questions,
                    setAnswers
                  );
                }
              }}
              autocompleteOnClick={(
                e,
                setSuggestionsVisible,
                setLocalAddress,
                place,
                locationList,
                setLocationList
              ) => {
                setSuggestionChoice(place.formatted_address);

                handleUserSelection(
                  place.formatted_address,
                  answer,
                  answers,
                  questions,
                  setAnswers
                );
              }}
              textFieldLabel={answer.a_error ? "Error" : answer.q_title}
              textFieldAriaLabel={"Enter address of the property for this loan"}
              searchOptionsTypes={[]}
              externalLocalAddress={address !== "" && address}
              error={answer.a_error}
            />
          </Box>
        );
        break;
      case "date":
        answerOptionsUI = (
          <Box
            key={`${index}_${answer.a_type}`}
            sx={{
              display: "flex",
              flexDirection: "column",
              margin: theme.spacing(1),
              fontSize: theme.spacing(2),
            }}
          >
            <>
              <Box>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    views={["month", "year"]}
                    label={"Date"}
                    value={
                      currentValues[answer.q_title] === undefined
                        ? null
                        : dayjs(currentValues[answer.q_title])
                    }
                    onChange={(newDate) => {
                      const newDateString = newDate
                        ? newDate.format("YYYY-MM")
                        : "";

                      const updatedCurrentValues = Object.assign(
                        {},
                        currentValues
                      );
                      updatedCurrentValues[answer.q_title] = newDateString;

                      setCurrentValues(updatedCurrentValues);

                      handleUserSelection(
                        newDateString,
                        answer,
                        answers,
                        questions,
                        setAnswers
                      );
                    }}
                    onBlur={(newDate) => {
                      const newDateString = newDate
                        ? newDate.format("YYYY-MM") // it needs this input to read in // but we like to display mm / yyyy in freedomlandia
                        : "";
                      processSubmission(newDateString);
                    }}
                    format="MM/YYYY"
                    renderInput={(params) => <TextField {...params} />}
                  />
                </LocalizationProvider>
              </Box>
            </>
          </Box>
        );
        break;
      default:
    }

    return answerOptionsUI;
  };

  const showError =
    loanQuoteData?.LTVObj &&
    (Object.keys(loanQuoteData.LTVObj).length === 0 ||
      creditScore <
        loanQuoteData.LTVObj[Object.keys(loanQuoteData.LTVObj)[0]]
          .creditScoreCutoff);

  const resetCalculator = () => {
    // Loop through all answers and set a_string to ""
    const updatedAnswers = answers.map((answer) => ({
      ...answer,
      a_string: "",
    }));

    // Reset current values to ""
    const updatedCurrentValues = Object.keys(currentValues).reduce(
      (acc, key) => {
        acc[key] = ""; // Set each current value to an empty string
        return acc;
      },
      {}
    );

    setCurrentValues(updatedCurrentValues); // Update current values in state
  };

  const saveOrUpdate = async (isPDFGenerated = false) => {
    const targetUserID = userProfile.UID;

    let savedOrUpdated = "saved";
    const calcData = {}; // not used for escrow

    let updatedCalc;
    if (!calc) {
      updatedCalc = await createCalc(
        user,
        targetUserID,
        answers,
        calcType,
        calcData,
        true,
        isPDFGenerated
      );
      // send an email to the user
      // with their calc details
      // so they have a way to return to it
      // this is done on the backend now, if query param l=1 is passed to createCalc
    } else {
      savedOrUpdated = "updated";
      const name = answers?.filter((answer) => {
        return answer?.q_title === "Calculation name";
      })?.[0]?.a_string;

      const address = answers?.filter((answer) => {
        return answer?.q_title === "Address (optional)";
      })?.[0]?.a_string;

      updatedCalc = await updateCalc(user, calc, {
        Answers: answers,
        CalcData: calcData,
        Name: name,
        Address: address,
        PDFGenerated: isPDFGenerated,
      });
    }

    setCalc(updatedCalc);

    if (!showUpdateFields) {
      setShowCreatedSuccessMessage(true);
    }

    if (!isPDFGenerated) {
      // only show this if not concurently generating the pdf
      setSnackbarData({
        message: `Calculation ${savedOrUpdated}`,
        options: {
          variant: "success",
          autoHideDuration: 5000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "center",
          },
        },
      });
    }

    gtag.event({ action: "escrow_calc-saved" });
  };

  return (
    <Box
      key={`calculation_${externalCalc?.id ? externalCalc.id : 0}`}
      sx={{
        display: "flex",
        flexDirection: "column",
        margin: theme.spacing(0, "auto"),
        width: "100%",
      }}
    >
      {router.pathname.includes("/my/calculation") && calc && (
        <Box sx={{ width: "100%", display: "flex" }}>
          <IconButton
            sx={{
              margin: theme.spacing("auto", 0.5, "auto", "auto"),
            }}
            key={`icon_button_delete_calc_${calc.id}`}
            aria-label="loan settings"
          >
            <MoreVertIcon
              key={`vert_icon_delete_calc_${calc.id}`}
              aria-controls={
                Boolean(anchorEl[calc.id]) ? "basic-menu" : undefined
              }
              aria-haspopup="true"
              aria-expanded={Boolean(anchorEl[calc.id]) ? "true" : undefined}
              onClick={(e) => handleOpen(e, calc)}
            />
            <Menu
              key={`menu_delete_calc_${calc.id}`}
              anchorEl={anchorEl[calc.id]}
              open={Boolean(anchorEl[calc.id])}
              onClose={() => handleClose(calc)}
              MenuListProps={{
                "aria-labelledby": "basic-button",
              }}
            >
              <MenuItem key={`menu_item_delete_${calc.id}`}>
                <Button
                  sx={{ width: "100%" }}
                  variant="contained"
                  color="error"
                  onClick={() => {
                    (async () => {
                      await updateCalc(user, calc, {
                        Deleted: true,
                      });

                      handleClose(calc);

                      await sleep(1000); // BE deleted flag need a sec to return on next call
                      await loadCalcs(refCalcs);
                    })();
                  }}
                >
                  Delete calculation
                </Button>
              </MenuItem>
            </Menu>
          </IconButton>
        </Box>
      )}
      <Button
        variant="outlined"
        color="secondary"
        onClick={resetCalculator}
        sx={{
          margin: theme.spacing(1, 1, 1, "auto"),
          width: "25%",
          [theme.breakpoints.down("md")]: {
            width: "33%",
            margin: theme.spacing(1, "auto", 1, 1),
          },
          "@media print": { display: "none" }, // Hide when printing
        }}
        className="no-print-element" // Add a class to identify it for PDF generation
      >
        Reset
      </Button>

      {answers.length > 0 ? (
        <Box id="calculator-to-print">
          <Box id="pdf-1-input">
            <QuestionRenderer
              answers={answers}
              questions={firstPartOfQuestions}
              theme={theme}
              showQuestionDescription={showQuestionDescription}
              setShowQuestionDescription={setShowQuestionDescription}
              currentValues={currentValues}
              generateAnswerOptions={generateAnswerOptions}
            />
          </Box>

          <Box
            id="pdf-2-total-loan"
            sx={{
              margin: theme.spacing(1, 0, 0, 0),
              padding: theme.spacing(1, 3),
              borderRadius: theme.spacing(1),
              border: `1px solid ${theme.palette.grey[300]}`,
              backgroundColor: theme.palette.background.paper,
              boxShadow: `0px 2px 4px ${theme.palette.grey[200]}`,
              [theme.breakpoints.down("md")]: {
                padding: theme.spacing(1),
              },
            }}
          >
            <Box
              sx={{
                margin: theme.spacing(1, 0, 0, 0),
                [theme.breakpoints.down("md")]: {
                  margin: theme.spacing(0, 0, 0, 0),
                },
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "row",
                  }}
                >
                  <Box
                    sx={{
                      flex: "0 1 50%",
                      display: "flex",
                      flexDirection: "row",
                    }}
                  >
                    <ButtonBase
                      sx={{
                        width: "100%",
                        padding: theme.spacing(1),
                        "&:focus": {
                          outline: "none",
                          borderRadius: theme.spacing(0.5),
                          boxShadow: `0 0 0 ${theme.spacing(0.5)} ${
                            theme.palette.info.main
                          }`,
                        },
                        [theme.breakpoints.down("md")]: {
                          margin: theme.spacing(1),
                          padding: 0,
                        },
                      }}
                      onClick={() => {
                        let updatedShowQuestionDescription = Object.assign(
                          {},
                          showQuestionDescription
                        );

                        updatedShowQuestionDescription[`Loan payment`] =
                          !updatedShowQuestionDescription[`Loan payment`];

                        setShowQuestionDescription(
                          updatedShowQuestionDescription
                        );
                      }}
                    >
                      <Typography
                        variant="h2"
                        sx={{
                          fontSize: theme.spacing(3),
                          textAlign: "left",
                          [theme.breakpoints.down("md")]: {
                            fontSize: theme.spacing(2),
                          },
                        }}
                      >
                        Loan payment
                      </Typography>
                      {showQuestionDescription[`Loan payment`] ? (
                        <KeyboardArrowUpIcon
                          sx={{
                            display: "flex",
                            margin: theme.spacing("auto", 1, "auto", "auto"),
                            fontSize: theme.spacing(2.5),
                          }}
                        />
                      ) : (
                        <InfoOutlinedIcon
                          sx={{
                            display: "flex",
                            margin: theme.spacing("auto", 1, "auto", "auto"),
                            fontSize: theme.spacing(2.5),
                          }}
                          color="info"
                        />
                      )}
                    </ButtonBase>
                  </Box>
                  <Box
                    sx={{
                      fontWeight: 600,
                      fontSize: theme.spacing(2.5),
                      textAlign: "center",
                      flex: "0 1 50%",
                      margin: "auto",
                    }}
                  >
                    ${formatMoney(loanPayment, 0)}{" "}
                    {currentValues["Payment frequency"]?.toLowerCase() ||
                      "/month"}
                  </Box>
                </Box>
                <Collapse in={showQuestionDescription[`Loan payment`]}>
                  <Box sx={{ width: "100%" }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        margin: theme.spacing(1),
                        fontSize: theme.spacing(1.8),
                        [theme.breakpoints.down("md")]: {
                          margin: theme.spacing(1, 0, 1, 1),
                        },
                      }}
                    >
                      Payment that will cover loan's principal and interest (P &
                      I) and prorated taxes and insurance
                    </Typography>
                  </Box>
                </Collapse>
              </Box>
            </Box>
            <Box
              sx={{
                margin: theme.spacing(1.5, 0),
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "row",
                  }}
                >
                  <Box
                    sx={{
                      flex: "0 1 50%",
                      display: "flex",
                      flexDirection: "row",
                    }}
                  >
                    <ButtonBase
                      sx={{
                        width: "100%",
                        padding: theme.spacing(1),
                        "&:focus": {
                          outline: "none",
                          borderRadius: theme.spacing(0.5),
                          boxShadow: `0 0 0 ${theme.spacing(0.5)} ${
                            theme.palette.info.main
                          }`,
                        },
                        [theme.breakpoints.down("md")]: {
                          margin: theme.spacing(1),
                          padding: 0,
                        },
                      }}
                      onClick={() => {
                        let updatedShowQuestionDescription = Object.assign(
                          {},
                          showQuestionDescription
                        );

                        updatedShowQuestionDescription[`Loan amount`] =
                          !updatedShowQuestionDescription[`Loan amount`];

                        setShowQuestionDescription(
                          updatedShowQuestionDescription
                        );
                      }}
                    >
                      <Typography
                        variant="h2"
                        sx={{
                          fontSize: theme.spacing(3),
                          textAlign: "left",
                          [theme.breakpoints.down("md")]: {
                            fontSize: theme.spacing(2),
                          },
                        }}
                      >
                        Loan amount
                      </Typography>
                      {showQuestionDescription[`Loan amount`] ? (
                        <KeyboardArrowUpIcon
                          sx={{
                            display: "flex",
                            margin: theme.spacing("auto", 1, "auto", "auto"),
                            fontSize: theme.spacing(2.5),
                          }}
                        />
                      ) : (
                        <InfoOutlinedIcon
                          sx={{
                            display: "flex",
                            margin: theme.spacing("auto", 1, "auto", "auto"),
                            fontSize: theme.spacing(2.5),
                          }}
                          color="info"
                        />
                      )}
                    </ButtonBase>
                  </Box>
                  <Box
                    sx={{
                      fontWeight: 600,
                      fontSize: theme.spacing(2.5),
                      textAlign: "center",
                      flex: "0 1 50%",
                      margin: "auto",
                    }}
                  >
                    ${formatMoney(loanAmount, 0)}
                  </Box>
                </Box>
                <Collapse in={showQuestionDescription[`Loan amount`]}>
                  <Box sx={{ width: "100%" }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        margin: theme.spacing(1),
                        fontSize: theme.spacing(1.8),
                        [theme.breakpoints.down("md")]: {
                          margin: theme.spacing(1, 0, 1, 1),
                        },
                      }}
                    >
                      The total amount of the loan
                    </Typography>
                  </Box>
                </Collapse>
              </Box>
            </Box>
          </Box>

          <Box id="pdf-3-pmts-2">
            <QuestionRenderer
              answers={answers}
              questions={secondPartOfQuestions}
              theme={theme}
              showQuestionDescription={showQuestionDescription}
              setShowQuestionDescription={setShowQuestionDescription}
              currentValues={currentValues}
              generateAnswerOptions={generateAnswerOptions}
            />
          </Box>
          <Box
            id="pdf-4-total-pmt-cash-to-close"
            sx={{
              margin: theme.spacing(1, 0, 0, 0),
              padding: theme.spacing(1, 3),
              borderRadius: theme.spacing(1),
              border: `1px solid ${theme.palette.grey[300]}`,
              backgroundColor: theme.palette.background.paper,
              boxShadow: `0px 2px 4px ${theme.palette.grey[200]}`,
              [theme.breakpoints.down("md")]: {
                padding: theme.spacing(1),
              },
            }}
          >
            <Box
              sx={{
                margin: theme.spacing(1, 0, 0, 0),
                padding: theme.spacing(1, 3),
                borderRadius: theme.spacing(1),

                [theme.breakpoints.down("md")]: {
                  padding: theme.spacing(1),
                },
              }}
            >
              <Box
                sx={{
                  margin: theme.spacing(1, 0, 0, 0),
                  [theme.breakpoints.down("md")]: {
                    margin: theme.spacing(0, 0, 0, 0),
                  },
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "row",
                    }}
                  >
                    <Box
                      sx={{
                        flex: "0 1 50%",
                        display: "flex",
                        flexDirection: "row",
                      }}
                    >
                      <ButtonBase
                        sx={{
                          width: "100%",
                          padding: theme.spacing(1),
                          "&:focus": {
                            outline: "none",
                            borderRadius: theme.spacing(0.5),
                            boxShadow: `0 0 0 ${theme.spacing(0.5)} ${
                              theme.palette.info.main
                            }`,
                          },
                          [theme.breakpoints.down("md")]: {
                            margin: theme.spacing(1),
                            padding: 0,
                          },
                        }}
                        onClick={() => {
                          let updatedShowQuestionDescription = Object.assign(
                            {},
                            showQuestionDescription
                          );

                          updatedShowQuestionDescription[`Total payment`] =
                            !updatedShowQuestionDescription[`Total payment`];

                          setShowQuestionDescription(
                            updatedShowQuestionDescription
                          );
                        }}
                      >
                        <Typography
                          variant="h2"
                          sx={{
                            fontSize: theme.spacing(3),
                            textAlign: "left",
                            [theme.breakpoints.down("md")]: {
                              fontSize: theme.spacing(2),
                            },
                          }}
                        >
                          Total payment
                        </Typography>
                        {showQuestionDescription[`Total payment`] ? (
                          <KeyboardArrowUpIcon
                            sx={{
                              display: "flex",
                              margin: theme.spacing("auto", 1, "auto", "auto"),
                              fontSize: theme.spacing(2.5),
                            }}
                          />
                        ) : (
                          <InfoOutlinedIcon
                            sx={{
                              display: "flex",
                              margin: theme.spacing("auto", 1, "auto", "auto"),
                              fontSize: theme.spacing(2.5),
                            }}
                            color="info"
                          />
                        )}
                      </ButtonBase>
                    </Box>
                    <Box
                      sx={{
                        fontWeight: 600,
                        fontSize: theme.spacing(2.5),
                        textAlign: "center",
                        flex: "0 1 50%",
                        margin: "auto",
                      }}
                    >
                      ${formatMoney(totalPayment, 0)}{" "}
                      {currentValues["Payment frequency"]?.toLowerCase() ||
                        "/month"}
                    </Box>
                  </Box>
                  <Collapse in={showQuestionDescription[`Total payment`]}>
                    <Box sx={{ width: "100%" }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          margin: theme.spacing(1),
                          fontSize: theme.spacing(1.8),
                          [theme.breakpoints.down("md")]: {
                            margin: theme.spacing(1, 0, 1, 1),
                          },
                        }}
                      >
                        Payment that will cover loan's principal and interest (P
                        & I) as well as prorated taxes and insurance
                      </Typography>
                    </Box>
                  </Collapse>
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                margin: theme.spacing(1, 0, 0, 0),
                padding: theme.spacing(1, 3),
                borderRadius: theme.spacing(1),
                [theme.breakpoints.down("md")]: {
                  padding: theme.spacing(1),
                },
              }}
            >
              <Box
                sx={{
                  margin: theme.spacing(1, 0, 0, 0),
                  [theme.breakpoints.down("md")]: {
                    margin: theme.spacing(0, 0, 0, 0),
                  },
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        flex: "0 1 50%",
                        display: "flex",
                        flexDirection: "row",
                      }}
                    >
                      <ButtonBase
                        sx={{
                          width: "100%",
                          padding: theme.spacing(1),
                          "&:focus": {
                            outline: "none",
                            borderRadius: theme.spacing(0.5),
                            boxShadow: `0 0 0 ${theme.spacing(0.5)} ${
                              theme.palette.info.main
                            }`,
                          },
                          [theme.breakpoints.down("md")]: {
                            margin: theme.spacing(1),
                            padding: 0,
                          },
                        }}
                        onClick={() => {
                          let updatedShowQuestionDescription = Object.assign(
                            {},
                            showQuestionDescription
                          );
                          updatedShowQuestionDescription[`Cash to close`] =
                            !updatedShowQuestionDescription[`Cash to close`];

                          setShowQuestionDescription(
                            updatedShowQuestionDescription
                          );
                        }}
                      >
                        <Typography
                          variant="h2"
                          sx={{
                            fontSize: theme.spacing(3),
                            textAlign: "left",
                            [theme.breakpoints.down("md")]: {
                              fontSize: theme.spacing(2),
                            },
                          }}
                        >
                          Cash to close
                        </Typography>
                        {showQuestionDescription[`Cash to close`] ? (
                          <KeyboardArrowUpIcon
                            sx={{
                              display: "flex",
                              margin: theme.spacing("auto", 1, "auto", "auto"),
                              fontSize: theme.spacing(2.5),
                            }}
                          />
                        ) : (
                          <InfoOutlinedIcon
                            sx={{
                              display: "flex",
                              margin: theme.spacing("auto", 1, "auto", "auto"),
                              fontSize: theme.spacing(2.5),
                            }}
                            color="info"
                          />
                        )}
                      </ButtonBase>
                    </Box>
                    <Box
                      sx={{
                        flex: "0 1 50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: theme.spacing(2),
                        [theme.breakpoints.down("md")]: {
                          flexDirection: "column",
                          gap: theme.spacing(1),
                        },
                      }}
                    >
                      <Box
                        sx={{
                          fontWeight: 600,
                          fontSize: theme.spacing(2.5),
                          textAlign: "center",
                          filter:
                            !userProfile ||
                            Object.keys(userProfile).length === 0
                              ? "blur(5px)"
                              : "none",
                        }}
                      >
                        ${formatMoney(cashToClose, 0)}
                      </Box>
                      {(!userProfile ||
                        Object.keys(userProfile).length === 0) && (
                        <Button
                          variant="contained"
                          color="success"
                          onClick={() => setShowLogInDialog(true)}
                          sx={{
                            whiteSpace: "nowrap",
                          }}
                        >
                          Sign in to see
                        </Button>
                      )}
                    </Box>
                  </Box>
                  <Collapse in={showQuestionDescription[`Cash to close`]}>
                    <Box sx={{ width: "100%" }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          margin: theme.spacing(1),
                          fontSize: theme.spacing(1.8),
                          filter:
                            !userProfile ||
                            Object.keys(userProfile).length === 0
                              ? "blur(5px)"
                              : "none",
                          [theme.breakpoints.down("md")]: {
                            margin: theme.spacing(1, 0, 1, 2),
                          },
                        }}
                      >
                        {`$${formatMoney(
                          parseFloat(currentValues["Purchase price"] || 0) -
                            loanAmount,
                          0
                        )}`}{" "}
                        Down payment
                        <br />+{" "}
                        {`$${formatMoney(
                          parseFloat(currentValues["Origination fee"] || 0),
                          0
                        )}`}{" "}
                        Origination fee
                        <br />+{" "}
                        {`$${formatMoney(
                          parseFloat(currentValues["Loan fees"] || 0),
                          0
                        )}`}{" "}
                        Loan fees
                        <br />+{" "}
                        {`$${formatMoney(
                          parseFloat(currentValues["Title fees"] || 0),
                          0
                        )}`}{" "}
                        Title fees
                        <br />+{" "}
                        {`$${formatMoney(
                          parseFloat(currentValues["Escrow"] || 0),
                          0
                        )}`}{" "}
                        Escrow
                        <br />+{" "}
                        {`$${formatMoney(
                          parseFloat(currentValues["Prepaid interest"] || 0),
                          0
                        )}`}{" "}
                        Prepaid interest
                        <br />+{" "}
                        {`$${formatMoney(
                          parseFloat(currentValues["Prepaid insurance"] || 0),
                          0
                        )}`}{" "}
                        Prepaid insurance
                        <br />={" "}
                        {!userProfile || Object.keys(userProfile).length === 0
                          ? `$0.00`
                          : `$${formatMoney(cashToClose, 0)}`}{" "}
                        Cash to close
                      </Typography>
                    </Box>
                  </Collapse>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box id="pdf-5-fees">
            <QuestionRenderer
              answers={answers}
              questions={thirdPartOfQuestions}
              theme={theme}
              showQuestionDescription={showQuestionDescription}
              setShowQuestionDescription={setShowQuestionDescription}
              currentValues={currentValues}
              generateAnswerOptions={generateAnswerOptions}
              calculatorType={calcType}
            />
          </Box>

          <Box id="pdf-6-save-share-pdf-questions">
            <SaveSharePDF
              answers={answers}
              allowedQuestionTitles={fourthPart}
              generateAnswerOptions={generateAnswerOptions}
              calc={calc}
              setCalc={setCalc}
              saveOrUpdate={saveOrUpdate}
              calculatorType={calcType}
            />
          </Box>

          <Box
            id="pdf-7-balloon-warning"
            sx={{
              margin: theme.spacing(2, 0, 0, 0),
            }}
          >
            {currentValues["Amortization type"] === "Interest only" && (
              <Alert
                severity="error"
                sx={{
                  margin: theme.spacing(2, 0),
                  "& .MuiAlert-message": {
                    width: "100%",
                  },
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                  Balloon Payment Warning
                </Typography>
                <Typography variant="body2">
                  This is an interest-only loan. A balloon payment of $
                  <b>
                    {formatMoney(
                      resultsData[resultsData.length - 1]?.payment || 0,
                      0
                    )}{" "}
                  </b>
                  will be due on the final payment date{" "}
                  <b>({resultsData[resultsData.length - 1]?.date})</b>. Your
                  regular {currentValues["Payment frequency"].toLowerCase()}{" "}
                  payments only cover interest and escrows.
                </Typography>
              </Alert>
            )}
          </Box>
          <Box
            sx={{
              margin: theme.spacing(1, 0, 0, 0),
              padding: theme.spacing(1, 0),
              [theme.breakpoints.down("md")]: {
                padding: theme.spacing(1),
              },
            }}
          >
            <Box>
              <Box sx={{ margin: theme.spacing(3, 0) }}>
                <Box id="pdf-8-payoff-chart">
                  <Typography
                    className="no-print-element"
                    variant="h5"
                    component="h2"
                    sx={{
                      fontWeight: "bold",
                      marginBottom: theme.spacing(2),
                      "@media print": { display: "none" },
                    }}
                  >
                    Payoff chart
                  </Typography>

                  <Box sx={{ width: "100%", height: 300 }}>
                    <PayoffChart data={resultsData} />
                  </Box>
                </Box>
                <Box id="pdf-9-amort-sched">
                  <Typography
                    className="no-print-element"
                    variant="h5"
                    component="h2"
                    sx={{
                      fontWeight: "bold",
                      marginBottom: theme.spacing(2),
                      "@media print": { display: "none" },
                    }}
                  >
                    Amortization schedule
                  </Typography>
                  <Box sx={{ position: "relative" }}>
                    <Box
                      sx={{
                        maxHeight: showFullSchedule
                          ? "none"
                          : theme.spacing(50),
                        overflow: "hidden",
                        position: "relative",
                        "&::after": !showFullSchedule
                          ? {
                              content: '""',
                              position: "absolute",
                              bottom: 0,
                              left: 0,
                              right: 0,
                              height: theme.spacing(20),
                              background:
                                "linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 100%)",
                              pointerEvents: "none",
                            }
                          : {},
                      }}
                    >
                      <AmortizationSchedule resultsData={resultsData} />
                    </Box>

                    {resultsData.length > 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          marginTop: theme.spacing(2),
                        }}
                      >
                        <Button
                          variant="outlined"
                          onClick={() => setShowFullSchedule(!showFullSchedule)}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: theme.spacing(1),
                          }}
                        >
                          {showFullSchedule ? "Show less" : "Show more"}
                          {showFullSchedule ? (
                            <KeyboardArrowUpIcon />
                          ) : (
                            <KeyboardArrowDownIcon />
                          )}
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{ margin: theme.spacing(2, 0), width: "100%", minHeigh: "100vh" }}
        >
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
        </Box>
      )}
      <Dialog
        open={showLogInDialog}
        onClose={() => setShowLogInDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent>
          <Authenticator customTitle={"Log in or sign up to do that"} />
        </DialogContent>
      </Dialog>
    </Box>
  );
}
